"use client"

import * as React from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Drawer, DrawerClose, DrawerContent, DrawerHeader } from "@/components/ui/drawer"
import MultistepForm from "@/components/valuation-form"

interface MultistepFormDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export default function MultistepFormDialog({ 
  isOpen, 
  onOpenChange 
}: MultistepFormDialogProps) {
  const [useFullScreen, setUseFullScreen] = React.useState(false)
  const [formKey, setFormKey] = React.useState(0)
  const autoCloseTimerRef = React.useRef<NodeJS.Timeout | null>(null)

  // Reset form when dialog closes to clear state on next open
  React.useEffect(() => {
    if (!isOpen) {
      // Clear any pending auto-close timer
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
      // Small delay to allow close animation to complete before resetting
      const timeoutId = setTimeout(() => {
        setFormKey(prev => prev + 1)
      }, 300)
      return () => clearTimeout(timeoutId)
    }
  }, [isOpen])

  // Determine full-screen (drawer) vs modal (dialog) mode based on viewport height
  React.useEffect(() => {
    if (typeof window === "undefined") return
    
    let timeoutId: NodeJS.Timeout | null = null
    
    const check = () => {
      // Clear any pending check
      if (timeoutId) clearTimeout(timeoutId)
      
      // Debounce resize events to prevent rapid state changes
      // Use full-screen drawer on taller viewports (>= 750px), modal dialog on shorter ones
      timeoutId = setTimeout(() => {
        setUseFullScreen(window.innerHeight <= 750)
      }, 150)
    }
    
    check()
    window.addEventListener("resize", check)
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
      window.removeEventListener("resize", check)
    }
  }, [])

  // Handle successful form submission - auto-close drawer after 5 seconds
  const handleSubmitSuccess = React.useCallback(() => {
    // Only auto-close in full-screen mode
    if (useFullScreen) {
      autoCloseTimerRef.current = setTimeout(() => {
        onOpenChange(false)
      }, 5000)
    }
  }, [useFullScreen, onOpenChange])

  // Render full-screen drawer for taller viewports
  if (useFullScreen) {
    return (
      <Drawer open={isOpen} onOpenChange={onOpenChange}>
        <DrawerContent>
          <DrawerHeader className="sr-only">
            Get Your Free Quote
          </DrawerHeader>
          <div className="flex items-center h-full">
            <MultistepForm key={formKey} dialog={false} onSubmitSuccess={handleSubmitSuccess} />
          </div>
          
          
        </DrawerContent>
      </Drawer>
    )
  }
  
  // Render centered modal dialog for shorter viewports
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col p-0" style={{zIndex: 10000}}>
        <DialogHeader className="px-6 hidden">
          <DialogTitle className="sr-only pt-0">Get Your Free Quote</DialogTitle>
          <DialogDescription className="sr-only">Fill out the form to receive a competitive quote for your luxury watch</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <MultistepForm key={formKey} dialog={true} />
        </div>
      </DialogContent>
    </Dialog>
  )
}
